import { LoadingTopProgress } from 'ctls/common/LoadingTopProgress';
import { LANG_KO_KR, langutil } from 'language/langutil';
import { DisplayPopupItem } from 'models/IfDisplayPopupItem';
import queryString from 'query-string';
import React, { useEffect } from 'react';
import TagManager from 'react-gtm-module';
import { HelmetProvider } from 'react-helmet-async';
import { RouterProvider } from 'react-router-dom';
import svcs from 'services';
import { useDisplayBottomPopup, useDisplayPopupItem } from 'store/reducers/useDisplayPopup';
import { GlobalStyle } from 'style/GlobalStyle';
import styled from 'styled-components';
import { SWRConfig } from 'swr';
import { cookieutil } from 'util/cookieutil';
import useChatbotWidget from 'hooks/useChatbotWidget';
import { isExistSessionLanguage } from 'util/sessionutil';
import { swrFetcher } from 'util/swrutil';
import { isMainPage } from 'util/urls';
import { useZstdInfo } from 'zstd/useZstdInfo';
import { useSetWinResize } from 'zstd/useZstdWinResize';
import { AppHelmet } from './AppHelmet';
import { createAppRouter } from './router/appRouter';

interface AppProps {}

const App = (appProps: AppProps) => {
  useSetWinResize();
  useChatbotWidget();
  const router = React.useMemo(createAppRouter, []);
  const { setIsLoginPopup } = useZstdInfo();
  const { setDisplayPopupItem } = useDisplayPopupItem();
  const { setDisplayBottomPopup } = useDisplayBottomPopup();

  const googleAnalyticsAccess = () => {
    const tagId = cookieutil.getGoogleTagId(window.location.host);

    const tagManagerArgs = {
      gtmId: tagId,
    };

    TagManager.initialize(tagManagerArgs);
  };

  if (window.location.host.includes('sol.ls-electric.com') && window.location.protocol === 'http:') {
    window.location.href = window.location.href.replace('http:', 'https:');
  }

  useEffect(() => {
    googleAnalyticsAccess();
    const popupItem = new DisplayPopupItem();
    const path = location.pathname;
    const language = svcs.svcLang.lang();
    const qs = queryString.parse(location.search);

    //login
    if (/^\/\w\w\/\w\w\/login/.test(path)) {
      setIsLoginPopup(true);
      //setShowLoginPopup(true);
    } else {
      //intro
      if (isMainPage(path)) {
        if (qs.terms) return;

        if (cookieutil.isAcceptFunctionConsentCookie(language)) {
          if (!window.localStorage.getItem('firstVisitedTime')) popupItem.intro = true;
        }
      }

      if (language != LANG_KO_KR) if (!cookieutil.countrySpecificCookieVerification(language)) popupItem.cookie = true;

      if (!isExistSessionLanguage('DISPLAY_CENTER_SESSION', language)) {
        popupItem.center = true;
      }

      const currentPath = window.location.pathname;
      const { country, lang } = langutil.splitLangCode(svcs.svcLang.lang());

      if (currentPath.startsWith('/main') || currentPath.startsWith(`/${country}/${lang}/main`))
        popupItem.centerMain = true;

      setDisplayPopupItem(popupItem);

      if (!isExistSessionLanguage('DISPLAY_BOTTOM_SESSION', language)) {
        setDisplayBottomPopup(true);
      }
    }
  }, []);

  return (
    <>
      <HelmetProvider>
        <GlobalStyle />
        <AppHelmet />
        <SWRConfig value={{ fetcher: swrFetcher, revalidateOnFocus: false, shouldRetryOnError: false }}>
          <AppContainer aria-label='app-container'>
            <LoadingTopProgress />
            <RouterProvider router={router} />
          </AppContainer>
        </SWRConfig>
      </HelmetProvider>
    </>
  );
};

export default App;

const AppContainer = styled.div`
  position: relative;
  height: 100%;

  .mobile {
    display: none;
  }

  &:has(.bottom-popup) .fixed-ft-btn {
    bottom: 12.8rem;
  }

  @media screen and (max-width: 1199px) {
    .mobile {
      display: block;
    }

    .non_mobile {
      display: none !important;
    }
  }

  @media screen and (max-width: 768px) {
    &:has(.commonity-footer-menu.active) {
      .fixed-ft-btn {
        bottom: 9rem;
      }

      .bottom-pop-active .fixed-ft-btn {
        bottom: calc(19.1rem + 2.8rem + 4rem);
      }

      .bottom-popup {
        bottom: 5.5rem;
      }
    }

    &:has(.bottom-popup) .fixed-ft-btn {
      bottom: calc(19.1rem + 2.8rem);
      right: 2.4rem;
    }
  }
`;
